import { Button, List } from '~/components/component-library'
import {
  ToastErrorIcon,
  ToastSuccessIcon,
} from '~/components/component-library/Toast'
import { StepButtons } from '~/components/create-workspace/StepButtons'
import {
  BuildInvitedRow,
  BuildOwnerRow,
} from '~/components/create-workspace/steps/3_OwnersStep'
import { RecoverySignerDisplay } from '~/components/create-workspace/steps/5_RecoveryStep/RecoverySignerDisplay'
import { ReviewStepOwnerStatus } from '~/components/create-workspace/steps/6_ReviewStep/ReviewStepOwnerStatus'
import { Networks } from '~/components/create-workspace/steps/6_ReviewStep/networks'
import { useReviewStepData } from '~/components/create-workspace/steps/6_ReviewStep/useReviewStepData'
import {
  useRequiredActiveOrg,
  useRequiredActiveOrgSmartAccountsSetup,
} from '~/hooks/org'
import { useRequiredAuthUser } from '~/hooks/useAuthUser'
import {
  getUserTreasuryPasskeyId,
  StepIndex,
} from '~/hooks/useSmartAccountSetupStep'
import { plural } from '~/utils/display'

export function ReviewStep({
  stepBack,
  stepForward,
  setStep,
  isSavingFinalStep,
}: {
  stepBack: () => void
  stepForward: () => void
  setStep: (step: StepIndex) => void
  isSavingFinalStep: boolean
}) {
  const authUser = useRequiredAuthUser()
  const setup = useRequiredActiveOrgSmartAccountsSetup()
  const org = useRequiredActiveOrg()
  const { owners, ownersMissingDetails, invitations } = useReviewStepData()

  const signerCount = setup.eoaSigners?.length ?? 0
  const hasMinimumSigners = signerCount >= 1
  const treasuryThreshold =
    setup?.thresholds?.find((t) => t.setupAccount === 'TREASURY')?.threshold ??
    1
  const recoveryThreshold =
    setup?.thresholds?.find((t) => t.setupAccount === 'ROOT')?.threshold ?? 1

  const finishButtonIsDisabled =
    ownersMissingDetails.length > 0 ||
    !hasMinimumSigners ||
    invitations.length > 0

  function getDisabledReason(): string | undefined {
    if (ownersMissingDetails.length > 0) {
      return 'Some owners are missing details'
    }
    if (!hasMinimumSigners) {
      return 'At least one recovery signer is required'
    }
    if (invitations.length > 0) {
      return 'Some invitations are pending'
    }
    return undefined
  }

  return (
    <>
      <div>
        <ReviewStepOwnerStatus />
        <div className="flex items-center gap-2">
          <span
            className={!hasMinimumSigners ? 'text-red-600' : 'text-green-600'}
          >
            {!hasMinimumSigners ? <ToastErrorIcon /> : <ToastSuccessIcon />}
          </span>
          <span className="text-primary">
            {!hasMinimumSigners
              ? 'At least one recovery signer is required'
              : `${signerCount} recovery ${plural('signer', signerCount)} set`}
          </span>
        </div>
      </div>
      <List
        label="Owners"
        secondaryDisplay={
          <Button variant="secondaryAction" onClick={() => setStep(2)}>
            Change
          </Button>
        }
        items={[
          ...(owners ?? []).map((o) =>
            BuildOwnerRow(o, {
              passkeyLen: org.passkeys.filter((p) => p.user?.id === o.id)
                .length,
              authUser,
              treasuryPasskeyId: getUserTreasuryPasskeyId(o.id, org),
            }),
          ),
          ...invitations.map((o) => BuildInvitedRow({ invitation: o })),
        ]}
        footer={`${treasuryThreshold} ${plural('approval', treasuryThreshold)} needed for Treasury transactions`}
      />
      <List
        label="Recovery signers"
        secondaryDisplay={
          <Button variant="secondaryAction" onClick={() => setStep(4)}>
            Change
          </Button>
        }
        items={(setup.eoaSigners ?? []).map((signer) => ({
          key: signer.id,
          content: (
            <RecoverySignerDisplay
              address={signer.address}
              name={signer.name}
              email={signer.email}
            />
          ),
          actionIcon: <div />,
        }))}
        footer={`${recoveryThreshold} ${plural('approval', recoveryThreshold)} needed to recover your workspace's assets`}
      />
      <Networks />
      <StepButtons
        onBackClick={stepBack}
        onNextClick={stepForward}
        isDisabled={finishButtonIsDisabled}
        isLoading={isSavingFinalStep}
        isLastStep
        disabledReason={getDisabledReason()}
      />
    </>
  )
}
